import type { FC, ReactNode } from 'react'
import { useCallback } from 'react'

import type { SxProps, Theme } from '@mui/material'
import {
  alpha,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'

import type { AgentMode } from '@/index'

import { AgentModeBadge } from '../agent-mode-badges'
import { CloseButton } from '@/common/components'
import { getDialogStyles } from './defaults'
import { createDialogPaperSx, createDialogSx, DEFAULT_DIALOG_WIDTH } from './styles'

interface BaseDialogProps {
  readonly open: boolean
  readonly onClose: () => void
  readonly title: string
  readonly breadcrumb?: string
  readonly children: ReactNode
  readonly agentMode?: AgentMode | null | undefined
  readonly agentModeBadge?: ReactNode
  readonly agentModeSelectable?: boolean
  readonly onAgentModeChange?: ((mode: AgentMode | null) => void) | undefined
  readonly dangerouslySkipPermissions?: boolean | undefined
  readonly autoModeSupported?: boolean | undefined
  readonly windowControlSlot1?: ReactNode
  readonly windowControlSlot2?: ReactNode
  readonly windowControlSlot3?: ReactNode
  readonly controlsRow?: ReactNode
  readonly statusRow?: ReactNode
  readonly fullScreen?: boolean
  readonly isExpanded?: boolean | undefined
  readonly fontSize?: string | undefined
  readonly dialogWidth?: string
  readonly dialogHeight?: string
}

interface DialogTitleBarProps {
  readonly title: string
  readonly breadcrumb: string | undefined
  readonly agentMode: AgentMode | null | undefined
  readonly agentModeBadge: ReactNode | undefined
  readonly agentModeSelectable: boolean
  readonly onAgentModeChange: ((mode: AgentMode | null) => void) | undefined
  readonly dangerouslySkipPermissions: boolean | undefined
  readonly autoModeSupported: boolean | undefined
  readonly windowControlSlot1: ReactNode | undefined
  readonly windowControlSlot2: ReactNode | undefined
  readonly windowControlSlot3: ReactNode | undefined
  readonly controlsRow: ReactNode | undefined
  readonly statusRow: ReactNode | undefined
  readonly onClose: () => void
}

interface AgentModeBadgeSectionProps {
  readonly agentMode?: AgentMode | null | undefined
  readonly agentModeBadge?: ReactNode
  readonly agentModeSelectable: boolean
  readonly onAgentModeChange?: ((mode: AgentMode | null) => void) | undefined
  readonly dangerouslySkipPermissions?: boolean | undefined
  readonly autoModeSupported?: boolean | undefined
}

interface TitleContentProps {
  readonly title: string
  readonly breadcrumb: string | undefined
  readonly agentMode: AgentMode | null | undefined
  readonly agentModeBadge: ReactNode | undefined
  readonly agentModeSelectable: boolean
  readonly onAgentModeChange: ((mode: AgentMode | null) => void) | undefined
  readonly dangerouslySkipPermissions: boolean | undefined
  readonly autoModeSupported: boolean | undefined
}

interface WindowControlsProps {
  readonly windowControlSlot1?: ReactNode
  readonly windowControlSlot2?: ReactNode
  readonly windowControlSlot3?: ReactNode
  readonly onClose: () => void
}

export const BaseDialog: FC<BaseDialogProps> = ({
  open,
  onClose,
  title,
  breadcrumb,
  children,
  agentMode,
  agentModeBadge,
  agentModeSelectable = false,
  onAgentModeChange,
  dangerouslySkipPermissions,
  autoModeSupported,
  windowControlSlot1,
  windowControlSlot2,
  windowControlSlot3,
  controlsRow,
  statusRow,
  fullScreen: fullScreenProp = false,
  isExpanded: isExpandedProp,
  fontSize,
  dialogWidth = DEFAULT_DIALOG_WIDTH,
  dialogHeight = '85vh',
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const handleClose = useCallback(
    (_event: unknown, reason?: string) => {
      if (reason === 'backdropClick') {
        return
      }
      onClose()
    },
    [onClose],
  )

  const isExpanded = isExpandedProp ?? false
  const fullScreen = fullScreenProp || isMobile || isExpanded

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      fullWidth={false}
      fullScreen={fullScreen}
      scroll="paper"
      sx={createDialogSx(theme, fullScreen, isExpanded, dialogWidth, dialogHeight)}
      PaperProps={{
        sx: createDialogPaperSx(theme),
        elevation: 0,
      }}
      slotProps={{
        backdrop: {
          sx: backdropSx(theme),
        },
      }}
    >
      <DialogTitleBar
        title={title}
        breadcrumb={breadcrumb}
        agentMode={agentMode}
        agentModeBadge={agentModeBadge}
        agentModeSelectable={agentModeSelectable}
        onAgentModeChange={onAgentModeChange}
        dangerouslySkipPermissions={dangerouslySkipPermissions}
        autoModeSupported={autoModeSupported}
        windowControlSlot1={windowControlSlot1}
        windowControlSlot2={windowControlSlot2}
        windowControlSlot3={windowControlSlot3}
        controlsRow={controlsRow}
        statusRow={statusRow}
        onClose={onClose}
      />

      <DialogContent sx={dialogContentSx(theme)}>
        <Box sx={childrenWrapperSx(fontSize)}>{children}</Box>
      </DialogContent>
    </Dialog>
  )
}

const DialogTitleBar: FC<DialogTitleBarProps> = ({
  title,
  breadcrumb,
  agentMode,
  agentModeBadge,
  agentModeSelectable,
  onAgentModeChange,
  dangerouslySkipPermissions,
  autoModeSupported,
  windowControlSlot1,
  windowControlSlot2,
  windowControlSlot3,
  controlsRow,
  statusRow,
  onClose,
}) => {
  const theme = useTheme()
  return (
    <DialogTitle sx={dialogTitleSx(theme)}>
      <Box sx={titleRowSx(!!controlsRow || !!statusRow)}>
        <TitleContent
          title={title}
          breadcrumb={breadcrumb}
          agentMode={agentMode}
          agentModeBadge={agentModeBadge}
          agentModeSelectable={agentModeSelectable}
          onAgentModeChange={onAgentModeChange}
          dangerouslySkipPermissions={dangerouslySkipPermissions}
          autoModeSupported={autoModeSupported}
        />
        <WindowControls
          windowControlSlot1={windowControlSlot1}
          windowControlSlot2={windowControlSlot2}
          windowControlSlot3={windowControlSlot3}
          onClose={onClose}
        />
      </Box>

      {controlsRow && <Box sx={controlsRowContainerSx}>{controlsRow}</Box>}

      {statusRow && <Box sx={statusRowContainerSx(!!controlsRow)}>{statusRow}</Box>}
    </DialogTitle>
  )
}

const TitleContent: FC<TitleContentProps> = ({
  title,
  breadcrumb,
  agentMode,
  agentModeBadge,
  agentModeSelectable,
  onAgentModeChange,
  dangerouslySkipPermissions,
  autoModeSupported,
}) => (
  <Box sx={titleContentSx}>
    <AgentModeBadgeSection
      agentMode={agentMode}
      agentModeBadge={agentModeBadge}
      agentModeSelectable={agentModeSelectable}
      onAgentModeChange={onAgentModeChange}
      dangerouslySkipPermissions={dangerouslySkipPermissions}
      autoModeSupported={autoModeSupported}
    />

    <Typography variant="h6" sx={titleSx}>
      {title}
    </Typography>

    {breadcrumb && (
      <Typography variant="body2" sx={breadcrumbSx}>
        {breadcrumb}
      </Typography>
    )}
  </Box>
)

const AgentModeBadgeSection: FC<AgentModeBadgeSectionProps> = ({
  agentMode,
  agentModeBadge,
  agentModeSelectable,
  onAgentModeChange,
  dangerouslySkipPermissions,
  autoModeSupported,
}) => {
  if (agentModeBadge) {
    return <Box sx={badgeWrapperSx}>{agentModeBadge}</Box>
  }

  if (agentMode !== undefined && agentMode !== null) {
    return (
      <Box sx={badgeWrapperSx}>
        <AgentModeBadge
          agentMode={agentMode}
          selectable={agentModeSelectable}
          onChange={onAgentModeChange}
          dangerouslySkipPermissions={dangerouslySkipPermissions}
          autoModeSupported={autoModeSupported}
        />
      </Box>
    )
  }

  return null
}

const WindowControls: FC<WindowControlsProps> = ({
  windowControlSlot1,
  windowControlSlot2,
  windowControlSlot3,
  onClose,
}) => (
  <Box sx={windowControlsContainerSx}>
    {windowControlSlot1}
    {windowControlSlot2}
    {windowControlSlot3}
    <CloseButton onClick={onClose} />
  </Box>
)

const dialogTitleSx = (theme: Theme): SxProps<Theme> => {
  const styles = getDialogStyles(theme.palette.mode)
  return {
    pt: styles.titleBar.paddingTop,
    pb: styles.titleBar.paddingBottom,
    borderBottom: `${styles.titleBar.borderBottomWidth}px solid ${styles.titleBar.borderBottomColor}`,
    flexShrink: 0,
    position: 'sticky',
    top: 0,
    backgroundColor: styles.titleBar.backgroundColor,
    zIndex: 1000,
  }
}

const titleRowSx = (hasControls: boolean): SxProps<Theme> => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  ...(hasControls && { mb: 3 }),
})

const titleContentSx: SxProps<Theme> = {
  flex: 1,
  pr: 2,
}

const badgeWrapperSx: SxProps<Theme> = {
  mb: 2,
}

const titleSx: SxProps<Theme> = {
  fontWeight: 600,
  fontSize: '1.25rem',
  mb: 1,
}

const breadcrumbSx: SxProps<Theme> = {
  color: 'text.secondary',
  fontSize: '0.875rem',
  fontWeight: 400,
  lineHeight: 1.5,
}

const windowControlsContainerSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 3,
}

const controlsRowContainerSx: SxProps<Theme> = {
  pt: 3,
}

const statusRowContainerSx = (hasControlsRow: boolean): SxProps<Theme> => ({
  pt: hasControlsRow ? 5 : 4,
  pb: 2,
})

const dialogContentSx = (theme: Theme): SxProps<Theme> => {
  const styles = getDialogStyles(theme.palette.mode)
  return {
    flex: 1,
    overflow: 'auto',
    pt: styles.content.paddingTop,
    px: styles.content.paddingHorizontal,
    pb: styles.content.paddingBottom,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: styles.content.backgroundColor,
  }
}

const childrenWrapperSx = (fontSize?: string): SxProps<Theme> => ({
  fontSize: fontSize,
})

const backdropSx = (theme: Theme): SxProps<Theme> => ({
  backgroundColor: alpha(
    theme.palette.mode === 'dark' ? theme.palette.common.black : '#3E2723',
    0.5,
  ),
})
